import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { Box, Stack, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  PersonalProperty,
  PersonalPropertyType,
  useGetValuableItemProtectionPriceLazyQuery,
} from "@covertree/new-business-graphql";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import {
  Button,
  Form,
  FormCurrencyField,
  FormSelectField,
  FormTextField,
} from "@covertree/ui";

import { scheduledPersonalPropertyValidation } from "../../validatorsAndTypes/extraCoveragesForm";
import { personalPropertyOptions } from "../../mappings/personalPropertyTypeMaps";
import { formatCurrency } from "@covertree/utils";
import {
  ModalTitleForValuables,
  PersonalPropertyWithPrice,
} from "./ScheduledPersonalPropertyCard";

interface AddOrEditPersonalPropertyProps {
  addOrUpdateItem(value: PersonalPropertyWithPrice): void;
  changeFormVisibilityAndCloseModal(shouldClose: boolean): void;
  currentItem?: PersonalProperty;
  updateModalTitle(title: ModalTitleForValuables): void;
}

export function AddOrEditPersonalProperty({
  addOrUpdateItem,
  changeFormVisibilityAndCloseModal,
  currentItem,
  updateModalTitle,
}: AddOrEditPersonalPropertyProps) {
  const { policyLocator } = useNewBusinessSelector(selectPolicy)!;

  const abortController = useRef(new AbortController());

  const [getValuableItemProtectionPrice, { loading, data, error }] =
    useGetValuableItemProtectionPriceLazyQuery({
      context: {
        fetchOptions: {
          signal: abortController.current.signal,
        },
      },
    });

  const {
    control,
    handleSubmit: submit,
    formState: { isDirty, isValid },
    reset,
    getValues,
    watch,
  } = useForm<PersonalPropertyWithPrice>({
    mode: "all",
    defaultValues: currentItem ?? {
      type: "" as PersonalPropertyType,
      description: "",
    },
    // @ts-expect-error Not idea why it errors out...
    resolver: yupResolver(scheduledPersonalPropertyValidation),
  });

  const itemType = watch("type");

  useEffect(() => {
    if (itemType.length > 0) {
      updateModalTitle(ModalTitleForValuables.NameAndValue);
    }
  }, [updateModalTitle, itemType]);

  const handleAddItem = useCallback(() => {
    addOrUpdateItem(getValues());
    updateModalTitle(ModalTitleForValuables.ValuableAdded);
    changeFormVisibilityAndCloseModal(false);
  }, [addOrUpdateItem, getValues]);

  const handleSubmit = useCallback(
    async (personalProperty: PersonalProperty) => {
      const { data } = await getValuableItemProtectionPrice({
        variables: {
          personalizedValuableItem: personalProperty.type,
          personalizedValuableName: personalProperty.description,
          personalizedValuableValue: personalProperty.value,
          policyLocator,
        },
      });

      reset({
        ...personalProperty,
        price: data?.getValuableItemProtectionPrice,
      });
    },
    [getValuableItemProtectionPrice, policyLocator, reset]
  );

  return (
    <Form onSubmit={submit(handleSubmit)}>
      <Stack spacing={2} mb={2}>
        <FormSelectField
          fullWidth
          control={control}
          watch={watch}
          disabled={loading}
          name="type"
          id="item-type"
          dataTestId="item-type"
          options={personalPropertyOptions}
          label="Type of item"
        />
        {itemType && (
          <Stack direction="row" spacing={2}>
            <FormTextField
              control={control}
              disabled={loading}
              name="description"
              dataTestId="item-name"
              id="item-name"
              label="Item name"
            />
            <FormCurrencyField
              control={control}
              disabled={loading}
              name="value"
              label="Item value"
              id="item-value"
              dataTestId="item-value"
            />
          </Stack>
        )}

        <Box>
          {error && (
            <Box
              sx={(theme) => ({
                padding: theme.spacing(2),
                width: "100%",
              })}>
              <Typography variant="h6" color="error" align="center">
                Something went wrong, while getting Valuable Item&apos;s price.
                Please try again.
              </Typography>
            </Box>
          )}
          {data?.getValuableItemProtectionPrice && (
            <Box
              sx={(theme) => ({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: theme.spacing(16),
                backgroundColor: "secondary.contrastText",
                borderRadius: theme.shape.borderRadius,
              })}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="h3" color="secondary" align="center">
                  {formatCurrency(data?.getValuableItemProtectionPrice / 12, 2)}
                </Typography>
                <Typography variant="h6" align="center">
                  Premium monthly
                </Typography>
                <Typography variant="subtitle1" align="center">
                  ({formatCurrency(data?.getValuableItemProtectionPrice, 2)}
                  /year)
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box display="flex" flexDirection="row">
          <Box sx={(theme) => ({ width: theme.spacing(20) })}>
            {isDirty || loading ? (
              <Button
                fullWidth
                type="button"
                data-testid="get-price-button"
                onClick={submit(handleSubmit)}
                loading={loading}
                disabled={!isValid || loading}>
                Get price
              </Button>
            ) : (
              <Button
                fullWidth
                type="button"
                data-testid="add-item-button"
                disabled={!isValid || !!error}
                onClick={handleAddItem}>
                {!!currentItem ? "Save" : "Add item"}
              </Button>
            )}
          </Box>
          <Box
            sx={(theme) => ({
              marginLeft: theme.spacing(2),
              width: theme.spacing(20),
            })}>
            <Button
              onClick={() => {
                abortController.current.abort();
                abortController.current = new AbortController();
                changeFormVisibilityAndCloseModal(true);
              }}
              variant="outlined"
              type="button"
              fullWidth>
              Cancel
            </Button>
          </Box>
        </Box>
      </Stack>
    </Form>
  );
}
